import { useEffect, useState, useCallback } from "react";
import { Bell, X, CheckCheck, MessageSquare, Package, Sparkles } from "lucide-react";
import api from "@/lib/api";

/**
 * NotificationBell — bell icon with unread count that opens a slide-in Inbox
 * showing the trader's most recent alerts (reminders, stock warnings, syncs).
 * Backed by GET /api/notifications + POST /api/notifications/read-all.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications?limit=50");
      setItems(r.data?.items || []);
      setUnread(r.data?.unread_count || 0);
    } catch {
      /* soft-fail — leave last-known state */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll unread count every 60s so the badge stays fresh without needing sockets.
  useEffect(() => {
    const t = setInterval(load, 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  // Iter34: also refresh instantly when the live bus fires a new notification
  // (reminders, low-stock warnings, sync events) so the badge doesn't lag by
  // up to 60 s behind reality.
  useEffect(() => {
    const onLive = (e) => {
      const kind = e?.detail?.kind;
      if (kind === "notification" || kind === "reminder_fired") load();
    };
    window.addEventListener("kobolog:live", onLive);
    return () => window.removeEventListener("kobolog:live", onLive);
  }, [load]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((prev) => prev.map((i) => ({ ...i, read_at: i.read_at || new Date().toISOString() })));
    } catch { /* ignore */ }
  };

  const markOneRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((i) => (i.id === id && !i.read_at ? { ...i, read_at: new Date().toISOString() } : i)));
      setUnread((n) => Math.max(0, n - 1));
    } catch { /* ignore */ }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); load(); }}
        className="relative w-10 h-10 rounded-full bg-white border border-border flex items-center justify-center hover:border-kudi-green/40 transition"
        data-testid="notification-bell"
        aria-label="Open notifications"
      >
        <Bell className="w-4 h-4 text-kudi-ink/70" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none border-2 border-white"
            data-testid="notification-bell-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="notification-inbox">
          <div
            className="absolute inset-0 bg-black/40 animate-in fade-in duration-200"
            onClick={() => setOpen(false)}
          />
          <div className="relative w-[92vw] sm:w-96 max-w-full h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right-4 duration-200">
            <div className="p-4 flex items-center justify-between border-b border-border">
              <div>
                <div className="font-heading text-lg font-bold">Inbox</div>
                <div className="text-[11px] text-kudi-ink/55">
                  {unread > 0 ? `${unread} unread` : "You're all caught up"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={markAllRead}
                  disabled={unread === 0}
                  className="text-[11px] font-semibold text-kudi-green flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-accent/40 disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="notification-mark-all-read"
                >
                  <CheckCheck className="w-3 h-3" />
                  Mark all read
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="w-8 h-8 rounded-full hover:bg-accent/40 flex items-center justify-center"
                  data-testid="notification-close"
                  aria-label="Close inbox"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading && items.length === 0 && (
                <div className="p-8 text-center text-sm text-kudi-ink/50">Loading…</div>
              )}
              {!loading && items.length === 0 && (
                <div className="p-8 text-center" data-testid="notification-empty">
                  <div className="w-12 h-12 mx-auto rounded-full bg-accent flex items-center justify-center mb-3">
                    <Bell className="w-5 h-5 text-kudi-green" />
                  </div>
                  <div className="text-sm font-semibold">No alerts yet</div>
                  <div className="text-[11px] text-kudi-ink/55 mt-1">
                    Reminders, low-stock warnings and account updates will show up here.
                  </div>
                </div>
              )}
              <ul className="divide-y divide-border/50">
                {items.map((n) => (
                  <NotificationRow key={n.id} n={n} onMarkRead={() => markOneRead(n.id)} />
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const KIND_META = {
  reminder: { icon: Bell, color: "text-amber-600 bg-amber-50" },
  stock_warning: { icon: Package, color: "text-red-600 bg-red-50" },
  sync: { icon: Sparkles, color: "text-blue-600 bg-blue-50" },
  general: { icon: MessageSquare, color: "text-kudi-green bg-accent" },
};

function NotificationRow({ n, onMarkRead }) {
  const meta = KIND_META[n.kind] || KIND_META.general;
  const Icon = meta.icon;
  const unread = !n.read_at;
  return (
    <li
      className={`p-4 flex gap-3 cursor-pointer hover:bg-accent/30 transition ${unread ? "bg-accent/10" : ""}`}
      onClick={onMarkRead}
      data-testid={`notification-item-${n.id}`}
    >
      <div className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${meta.color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold flex items-center gap-2">
          <span className="truncate">{n.title}</span>
          {unread && <span className="w-2 h-2 rounded-full bg-kudi-green shrink-0" aria-hidden />}
        </div>
        {n.body && <div className="text-[11px] text-kudi-ink/60 mt-0.5 line-clamp-2">{n.body}</div>}
        <div className="text-[10px] text-kudi-ink/40 mt-1">
          {formatWhen(n.created_at)}{n.channel ? ` · via ${n.channel}` : ""}
        </div>
      </div>
    </li>
  );
}

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString("en-NG", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}
